import React, { useState, useEffect } from 'react';
import Icon from './AppIcon';
import { supabase } from '../lib/supabase';

// ── helpers ──────────────────────────────────────────────────────────────────

function parseStrongsNumber(raw) {
  if (!raw) return { eStrongKey: '', disambigSuffix: '', instanceSuffix: '' };
  let s = raw?.trim();
  let instanceSuffix = '';
  const instanceMatch = s?.match(/^([HG]\d+[A-Za-z]*)_([A-Za-z]+)$/);
  if (instanceMatch) { s = instanceMatch?.[1]; instanceSuffix = instanceMatch?.[2]; }
  const upperSuffixMatch = s?.match(/^([HG]\d+)([A-Z])$/);
  if (upperSuffixMatch) {
    return { eStrongKey: upperSuffixMatch?.[1], disambigSuffix: upperSuffixMatch?.[2], instanceSuffix };
  }
  return { eStrongKey: s, disambigSuffix: '', instanceSuffix };
}

/**
 * Pads a Hebrew Strong's number to 4 digits for STEPBible e_strong queries.
 * H216 → H0216, H1234 → H1234, G123 → G123 (Greek unchanged)
 */
function padStrongsForSTEP(key) {
  if (!key) return key;
  let m = key?.match(/^(H)(\d+)([A-Za-z]*)$/);
  if (m) {
    const padded = m?.[2]?.padStart(4, '0');
    return `${m?.[1]}${padded}${m?.[3]}`;
  }
  return key;
}

/**
 * Strips leading zeros from a Hebrew Strong's number for Traditional queries.
 * H0216 → H216, H1234 → H1234
 */
function stripLeadingZerosForTraditional(key) {
  if (!key) return key;
  let m = key?.match(/^(H)0*(\d+)([A-Za-z]*)$/);
  if (m) {
    return `${m?.[1]}${m?.[2]}${m?.[3]}`;
  }
  return key;
}

function parseMeaning(raw) {
  if (!raw) return [];
  const plain = raw?.replace(/<br\s*\/?>/gi, ' ')?.replace(/<[^>]+>/g, '');
  const markerRegex = /([1-9](?:[a-z][1-9])*[a-z]?)\)/g;
  const matches = [];
  let m;
  while ((m = markerRegex?.exec(plain)) !== null) {
    matches?.push({ key: m?.[1], start: m?.index, end: m?.index + m?.[0]?.length });
  }
  if (matches?.length === 0) {
    const text = plain?.trim();
    return text ? [{ level: 0, label: '', text }] : [];
  }
  const pairs = [];
  for (let i = 0; i < matches?.length; i++) {
    const contentStart = matches?.[i]?.end;
    const contentEnd = i + 1 < matches?.length ? matches?.[i + 1]?.start : plain?.length;
    const content = plain?.slice(contentStart, contentEnd)?.trim();
    pairs?.push({ key: matches?.[i]?.key, content });
  }
  const result = [];
  for (const { key, content } of pairs) {
    const segments = key?.match(/[a-z]|[1-9]/g) || [];
    const depth = segments?.length;
    const lastChar = key?.[key?.length - 1];
    const label = lastChar + ')';
    if (content && content !== '---') {
      result?.push({ level: depth, label, text: content });
    } else if (!content || content === '---') {
      if (content !== '---') {
        result?.push({ level: depth, label, text: '' });
      }
    }
  }
  return result;
}

function MeaningDisplay({ meaning }) {
  const lines = parseMeaning(meaning);
  if (!lines?.length) return null;
  const labelColors = ['text-amber-400','text-blue-400','text-green-400','text-orange-400','text-rose-400','text-muted-foreground'];
  const borderColors = ['border-amber-400','border-blue-400','border-green-400','border-orange-400','border-rose-400','border-border'];
  return (
    <div className="space-y-1">
      {lines?.map((line, i) => {
        const clampedLevel = Math.min(line?.level - 1, labelColors?.length - 1);
        const indent = (line?.level - 1) * 16;
        const labelColor = labelColors?.[clampedLevel] || 'text-muted-foreground';
        const border = borderColors?.[clampedLevel] || 'border-border';
        return (
          <div key={i} className="flex items-start gap-1.5" style={{ paddingLeft: `${indent}px` }}>
            {line?.level > 1 && (
              <span className={`flex-shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full border-2 ${border} bg-card`} />
            )}
            {line?.label && (
              <span className={`flex-shrink-0 text-xs font-semibold ${labelColor} mt-0.5`}>{line?.label}</span>
            )}
            {line?.text && (
              <span className={`text-xs leading-relaxed ${line?.level === 1 ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>
                {line?.text}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── STEPBible tab content ─────────────────────────────────────────────────────

function StepBibleContent({ strongsNumber }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { eStrongKey } = parseStrongsNumber(strongsNumber);
  const isHebrew = eStrongKey?.startsWith('H');
  const tableName = isHebrew ? 'lexicon_hebrew' : 'lexicon_greek';
  const wordField = isHebrew ? 'hebrew_form' : 'greek_form';
  // STEPBible e_strong uses zero-padded Hebrew numbers (H0216 not H216)
  const stepbibleKey = padStrongsForSTEP(eStrongKey);

  useEffect(() => {
    if (!eStrongKey) return;
    const fetch = async () => {
      setLoading(true);
      setError(null);
      try {
        const selectFields = `id, e_strong, d_strong, u_strong, transliteration, morph, gloss, meaning, ${wordField}`;
        const { data, error: dbError } = await supabase
          ?.from(tableName)
          ?.select(selectFields)
          ?.eq('e_strong', stepbibleKey)
          ?.order('d_strong', { ascending: true });
        if (dbError) throw dbError;
        setEntries(data || []);
      } catch (err) {
        console.error('STEPBible lexicon fetch error:', err);
        setError('Failed to load STEPBible entry.');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [eStrongKey, tableName, wordField, stepbibleKey]);

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 py-6 text-muted-foreground">
        <div className="w-3 h-3 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        <span className="text-xs">Loading STEPBible entry…</span>
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex items-center gap-2 py-4 text-red-400 text-xs">
        <Icon name="AlertCircle" size={14} />
        <span>{error}</span>
      </div>
    );
  }
  if (!entries?.length) {
    return (
      <div className="flex flex-col items-center gap-2 py-6 text-muted-foreground">
        <Icon name="BookOpen" size={20} />
        <span className="text-xs">No STEPBible entry found for {eStrongKey}.</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {entries?.map((entry, idx) => (
        <div key={entry?.id || idx} className={entries?.length > 1 ? 'border border-border rounded-lg p-3 bg-background/50' : ''}>
          {/* Word form + transliteration + gloss */}
          <div className="flex flex-wrap items-baseline gap-1.5 mb-1.5">
            {entry?.[wordField] && (
              <span
                className={`font-bold text-foreground ${isHebrew ? 'text-xl' : 'text-lg'}`}
                dir={isHebrew ? 'rtl' : 'ltr'}
                style={isHebrew ? { fontFamily: 'serif' } : {}}
              >
                {entry?.[wordField]}
              </span>
            )}
            {entry?.transliteration && (
              <span className="text-sm italic text-muted-foreground">{entry?.transliteration}</span>
            )}
            {entry?.gloss && (
              <span className="text-sm font-semibold text-primary">&ldquo;{entry?.gloss}&rdquo;</span>
            )}
          </div>
          {/* badges */}
          <div className="flex flex-wrap gap-1 mb-2">
            {entry?.d_strong && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-mono font-semibold bg-primary/20 text-primary">
                {entry?.d_strong}
              </span>
            )}
            {entry?.morph && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-mono bg-muted text-muted-foreground">
                {entry?.morph}
              </span>
            )}
          </div>
          {/* Meaning */}
          {entry?.meaning && <MeaningDisplay meaning={entry?.meaning} />}
        </div>
      ))}
    </div>
  );
}

// ── Traditional Strong's tab content ─────────────────────────────────────────

function TraditionalContent({ definition }) {
  const createMarkup = (html) => ({ __html: html || '' });
  const renderText = (text) => text ? <span dangerouslySetInnerHTML={createMarkup(text)} /> : null;

  if (!definition) {
    return (
      <div className="flex flex-col items-center gap-2 py-6 text-muted-foreground">
        <Icon name="BookOpen" size={20} />
        <span className="text-xs">No Traditional Strong's entry found.</span>
      </div>
    );
  }

  const { number, rootWord, transliteration, pronunciation, entry, tvm } = definition;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <span className="text-base font-bold text-foreground">{number}</span>
      </div>
      {(rootWord || transliteration || pronunciation) && (
        <div className="flex flex-wrap items-center gap-1 text-sm">
          {rootWord && <span className="font-semibold text-foreground">{renderText(rootWord)}</span>}
          {(rootWord && (transliteration || pronunciation)) && <span className="text-border">|</span>}
          {transliteration && <span className="italic text-muted-foreground">{renderText(transliteration)}</span>}
          {(transliteration && pronunciation) && <span className="text-border">|</span>}
          {pronunciation && <span className="text-muted-foreground">{renderText(pronunciation)}</span>}
        </div>
      )}
      {entry && (
        <div className="text-sm text-foreground leading-relaxed">
          <div dangerouslySetInnerHTML={createMarkup(entry)} className="strongs-entry" />
        </div>
      )}
      {tvm && (
        <div className="text-sm text-foreground leading-relaxed border-t border-border pt-2">
          <p className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Tense / Voice / Mood</p>
          <div dangerouslySetInnerHTML={createMarkup(tvm)} className="strongs-entry" />
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

const StrongsNumberCard = ({ definition, isVisible, onClose, position = {} }) => {
  const [activeTab, setActiveTab] = useState('stepbible');
  const [mounted, setMounted] = useState(false);

  // Reset to default tab when a new card opens
  useEffect(() => {
    if (isVisible) {
      setActiveTab('stepbible');
      // Trigger mount animation
      requestAnimationFrame(() => setMounted(true));
    } else {
      setMounted(false);
    }
  }, [isVisible, definition?.number]);

  if (!isVisible || !definition) return null;

  const { number } = definition;
  const { eStrongKey, disambigSuffix } = parseStrongsNumber(number);
  const isHebrew = eStrongKey?.startsWith('H');

  const tabs = [
    { id: 'stepbible', label: 'STEPBible Strongs' },
    { id: 'traditional', label: "Traditional Strong\'s" },
  ];

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/40 transition-opacity duration-200"
        style={{ opacity: mounted ? 1 : 0 }}
        onClick={onClose}
      />

      {/* Card */}
      <div
        className="fixed z-50 bg-card border border-border rounded-xl shadow-2xl w-80 flex flex-col transform -translate-x-1/2 transition-all duration-200"
        style={{
          left: position?.x || '50%',
          top: Math.max(10, (position?.y || 100) - 10),
          maxHeight: '70vh',
          opacity: mounted ? 1 : 0,
          transform: `translateX(-50%) translateY(${mounted ? '0px' : '-8px'}) scale(${mounted ? 1 : 0.96})`,
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-primary" />
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {isHebrew ? 'Hebrew' : 'Greek'} — Strong's
            </span>
            {disambigSuffix ? (
              <span className="text-sm font-bold text-foreground">
                {eStrongKey}<span className="text-primary">{disambigSuffix}</span>
              </span>
            ) : (
              <span className="text-sm font-bold text-foreground">{eStrongKey || number}</span>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-all duration-150 rounded-full p-0.5 hover:bg-muted hover:scale-110 active:scale-95"
            aria-label="Close definition"
          >
            <Icon name="X" size={15} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border px-4 flex-shrink-0">
          {tabs?.map(tab => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`py-2 px-0.5 mr-4 text-xs font-medium border-b-2 transition-all duration-150 whitespace-nowrap hover:scale-105 active:scale-95 ${
                activeTab === tab?.id
                  ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              }`}
            >
              {tab?.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-4 py-3">
          {activeTab === 'stepbible' && (
            <StepBibleContent strongsNumber={number} />
          )}
          {activeTab === 'traditional' && (
            <TraditionalContent definition={definition} />
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t border-border bg-background/50 rounded-b-xl flex-shrink-0">
          {activeTab === 'stepbible' ? (
            <p className="text-[9px] text-muted-foreground">STEPBible.org / Tyndale House Cambridge (CC BY 4.0)</p>
          ) : (
            <p className="text-[9px] text-muted-foreground">Traditional Strong's Concordance</p>
          )}
        </div>

        {/* Arrow pointing down */}
        <div
          className="absolute w-3 h-3 bg-card border-l border-t border-border transform rotate-45 -translate-x-1/2"
          style={{ left: '50%', bottom: '-6px' }}
        />
      </div>
    </>
  );
};

export default StrongsNumberCard;